import { Button, Dropdown, HStack, Spacer } from '@sgme/ui';
import clsx from 'clsx';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { OptionContext, useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useQueuedDispatch } from '@/App/tiles/Option/QueueContext.tsx';
import { StripPopup } from '@/App/tiles/Option/strategy/strip/StripPopup.tsx';
import { parseStrike } from '@/App/tiles/Option/utils/strikeUtils.ts';
import { Import } from '@/App/tiles/shared/import/Import';
import {
  generateValidators,
  type ImportedStrategyRow,
  mapStrategyRow,
  requiredColumns,
  type StrategyParseError,
} from '@/App/tiles/shared/import/strategy';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { selectOptionCurrency } from '@/store/option/selectors/optionSelectors.ts';
import { optionSlice } from '@/store/option/slice.ts';
import type { OptionLegProperties } from '@/store/option/state.ts';
import { replaceOptionLegsThunk } from '@/store/option/thunks/thunk.ts';
import { selectActiveTabZoomedTileId, setZoomedTileIdForActiveTab } from '@/store/tabs/slice.ts';

export const StrategyPanelHeader = () => {
  const dispatch = useAppDispatch();
  const queuedDispatch = useQueuedDispatch();
  const tileId = useContext(OptionContext);
  const [showStrip, setShowStrip] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const isZoomed = useAppSelector(selectActiveTabZoomedTileId) === tileId;

  const { legs, strategyPanelMode, groupedByExpiry, currency } = useOptionSelector((option) => ({
    legs: option.properties.legs,
    strategyPanelMode: option.properties.uiState.strategyPanelMode,

    groupedByExpiry: option.properties.uiState.groupLegsByExpiry,
    currency: selectOptionCurrency(option),
  }));

  function toggleZoom() {
    dispatch(setZoomedTileIdForActiveTab({ tileId: isZoomed ? undefined : tileId }));
  }

  const toggleStrategyPanelMode = useCallback(() => {
    dispatch(
      optionSlice.actions.setStrategyPanelMode({
        tileId,
        mode: strategyPanelMode === 'vertical' ? 'horizontal' : 'vertical',
      }),
    );
  }, [tileId, strategyPanelMode]);

  const toggleGroupedByExpiry = useCallback(() => {
    dispatch(
      optionSlice.actions.toggleGroupedByExpiry({
        tileId,
      }),
    );
  }, [tileId]);

  const [importModalOpen, setImportModalOpen] = useState(false);

  function showImportModal() {
    setImportModalOpen(true);
  }

  async function onStrategyImport(rows: ImportedStrategyRow[]) {
    const legs: OptionLegProperties[] = rows.map((row, index) => {
      const legProductName = getStrategyLegProductName(row.product);

      return {
        legId: `${tileId}/${index}`,
        legProductName,
        side: row.way === 'buy' ? 'Buy' : 'Sell',
        type: row.type === 'put' ? 'Put' : 'Call',
        expiryDate: row.expiryDate,
        strike: parseStrike(row.strike),
        notional: row.notional as unknown as number,
        notionalCurrencyIndex: row.notionalCcy === currency.currency2 ? 2 : 1,
        deliveryDate: row.deliveryDate,
      };
    });
    await queuedDispatch(replaceOptionLegsThunk({ tileId, legs }));
  }
  const validators = useMemo(() => {
    return generateValidators(currency.currencyPair);
  }, [currency.currencyPair]);

  return (
    <div ref={ref} className={clsx('d-flex', 'flex-between ps-2', 'bg-lvl1', 'border-bottom')}>
      <FormattedMessage id="option.strategyPanelHeader.title" values={{ nbLegs: legs.length }} />
      <Spacer />
      <HStack>
        <Button iconPosition="start" purpose="flat" color="secondary" size="md" onClick={toggleZoom}>
          <em className="icon">zoom_out_map</em>
          <FormattedMessage id="option.strategyPanelHeader.zoom" />
        </Button>
        <Dropdown color="secondary" direction="auto" menuAlignment="end" size="md" purpose="flat">
          <Dropdown.Toggle className="btn-icon-end" data-e2e="legDisplay-btn">
            <FormattedMessage id="option.strategyPanelHeader.actions" />
          </Dropdown.Toggle>

          <Dropdown.Menu>
            <Dropdown.Item data-e2e="strip-button" onClick={() => setShowStrip(true)}>
              <i className="icon pe-4px">view_column</i>
              <FormattedMessage id="option.strategyPanelHeader.actions.strip" />
            </Dropdown.Item>
            <Dropdown.Item data-e2e="import-csv-button" onClick={showImportModal}>
              <i className="icon pe-4px">attach_file</i>
              <FormattedMessage id="option.strategyPanelHeader.actions.import" />
            </Dropdown.Item>
            <Dropdown.Separator />
            <Dropdown.Item
              onClick={toggleStrategyPanelMode}
              state={strategyPanelMode === 'horizontal' ? 'active' : undefined}
            >
              <FormattedMessage id="option.strategyPanelHeader.actions.displayLegsHorizontally" />
            </Dropdown.Item>
            <Dropdown.Separator />
            <Dropdown.Item state={groupedByExpiry ? 'active' : undefined} onClick={toggleGroupedByExpiry}>
              <FormattedMessage id="option.strategyPanelHeader.actions.groupByExpiry" />
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </HStack>

      <StripPopup target={ref} show={showStrip} onToggle={setShowStrip} />

      <Import<ImportedStrategyRow, StrategyParseError>
        mapRowWith={mapStrategyRow}
        requiredColumns={requiredColumns}
        onImport={onStrategyImport}
        validators={validators}
        isEmpty={false}
        isOpen={importModalOpen}
        close={() => setImportModalOpen(false)}
        tradeType="Option"
      />
    </div>
  );
};

function getStrategyLegProductName(product: ImportedStrategyRow['product']): OptionLegProperties['legProductName'] {
  if (product === 'trigger') {
    return 'Trigger';
  }

  if (product === 'binary') {
    return 'Binary';
  }

  return 'Vanilla';
}
